// Admin "Cached status" view for the 8-page LLB tutor notes cache.
//
// Surfaces, for every module in LLB_MODULES, whether a cached PDF exists,
// when it was last refreshed, and how large it is. Provides quick actions
// to invalidate, refresh, preview, and inspect the public health endpoint.

import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  adminListLlbNotesCache,
  adminInvalidateLlbNote,
  adminRefreshLlbNote,
} from "@/lib/api/llb-notes-cache.functions";
import { Loader2, RefreshCw, Eye, Trash2, ExternalLink, Database } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/portal/admin/llb-notes-cache")({
  component: CachedStatusPage,
});

function fmtBytes(b: number) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}
function fmtAgo(iso: string | null) {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function CachedStatusPage() {
  const listFn = useServerFn(adminListLlbNotesCache);
  const invalidateFn = useServerFn(adminInvalidateLlbNote);
  const refreshFn = useServerFn(adminRefreshLlbNote);
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<"all" | "cached" | "missing">("all");

  const cacheQuery = useQuery({
    queryKey: ["admin-llb-notes-cache"],
    queryFn: () => listFn(),
    staleTime: 30_000,
  });

  const invalidate = useMutation({
    mutationFn: (code: string) => invalidateFn({ data: { moduleCode: code } }),
    onSuccess: (_d, code) => { toast.success(`Cleared cache for ${code}`); qc.invalidateQueries({ queryKey: ["admin-llb-notes-cache"] }); },
    onError: (e: any) => toast.error(e?.message ?? "Failed to clear cache"),
  });
  const refresh = useMutation({
    mutationFn: (code: string) => refreshFn({ data: { moduleCode: code } }),
    onSuccess: (_d, code) => { toast.success(`Regenerated ${code}`); qc.invalidateQueries({ queryKey: ["admin-llb-notes-cache"] }); },
    onError: (e: any) => toast.error(e?.message ?? "Failed to regenerate"),
  });

  const rows = cacheQuery.data?.rows ?? [];
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "cached" && !r.cached) return false;
      if (filter === "missing" && r.cached) return false;
      if (!needle) return true;
      return r.code.toLowerCase().includes(needle) || r.title.toLowerCase().includes(needle);
    });
  }, [rows, q, filter]);

  const cachedCount = cacheQuery.data?.cachedCount ?? 0;
  const total = cacheQuery.data?.total ?? rows.length;
  const pct = total ? Math.round((cachedCount / total) * 100) : 0;

  return (
    <div className="container max-w-6xl py-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Database className="h-6 w-6" /> Cached notes status</h1>
          <p className="text-sm text-muted-foreground">Which LLB module 8-page tutor notes are currently cached, and when each was last refreshed.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => qc.invalidateQueries({ queryKey: ["admin-llb-notes-cache"] })} disabled={cacheQuery.isFetching}>
          {cacheQuery.isFetching ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Reload
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card><CardHeader className="pb-2"><CardDescription>Modules</CardDescription><CardTitle className="text-2xl">{total}</CardTitle></CardHeader></Card>
        <Card><CardHeader className="pb-2"><CardDescription>Cached</CardDescription><CardTitle className="text-2xl">{cachedCount}</CardTitle></CardHeader></Card>
        <Card><CardHeader className="pb-2"><CardDescription>Coverage</CardDescription><CardTitle className="text-2xl">{pct}%</CardTitle></CardHeader></Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle>Modules</CardTitle>
            <CardDescription>Cache state and last refresh time per module.</CardDescription>
          </div>
          <div className="flex gap-2 items-center">
            <Input placeholder="Search by code or title…" value={q} onChange={(e) => setQ(e.target.value)} className="w-64" />
            <div className="flex gap-1 rounded-md border p-0.5 text-xs">
              {(["all", "cached", "missing"] as const).map((f) => (
                <button key={f} onClick={() => setFilter(f)} className={`px-2.5 py-1 rounded ${filter === f ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>
                  {f[0].toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {cacheQuery.isLoading ? (
            <div className="py-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading cache…</div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">No modules match.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground border-b">
                  <tr>
                    <th className="text-left py-2 pr-2">Code</th>
                    <th className="text-left py-2 pr-2">Title</th>
                    <th className="text-left py-2 pr-2">Level</th>
                    <th className="text-left py-2 pr-2">Cached</th>
                    <th className="text-left py-2 pr-2">Size</th>
                    <th className="text-left py-2 pr-2">Last refreshed</th>
                    <th className="text-right py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.code} className="border-b hover:bg-muted/30">
                      <td className="py-2 pr-2 font-mono font-semibold">{r.code}</td>
                      <td className="py-2 pr-2">{r.title}</td>
                      <td className="py-2 pr-2">{r.level}</td>
                      <td className="py-2 pr-2">
                        {r.cached ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">cached</Badge> : <Badge variant="outline" className="text-amber-700 border-amber-300">missing</Badge>}
                      </td>
                      <td className="py-2 pr-2 text-muted-foreground">{fmtBytes(r.bytes)}</td>
                      <td className="py-2 pr-2 text-muted-foreground" title={r.cachedAt ?? ""}>{fmtAgo(r.cachedAt)}</td>
                      <td className="py-2 text-right whitespace-nowrap">
                        <a className="inline-flex" href={`/api/public/llb-module-notes/${r.code}?format=html`} target="_blank" rel="noreferrer">
                          <Button variant="ghost" size="sm" title="Preview HTML"><Eye className="h-4 w-4" /></Button>
                        </a>
                        <a className="inline-flex" href={`/api/public/llb-module-notes-health/${r.code}?format=html`} target="_blank" rel="noreferrer">
                          <Button variant="ghost" size="sm" title="Health endpoint"><ExternalLink className="h-4 w-4" /></Button>
                        </a>
                        <Button variant="ghost" size="sm" title="Regenerate" onClick={() => refresh.mutate(r.code)} disabled={refresh.isPending && refresh.variables === r.code}>
                          {refresh.isPending && refresh.variables === r.code ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" title="Invalidate cache" onClick={() => invalidate.mutate(r.code)} disabled={!r.cached || (invalidate.isPending && invalidate.variables === r.code)}>
                          {invalidate.isPending && invalidate.variables === r.code ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        See also <Link to="/portal/admin/llb-modules" className="underline">LLB module settings</Link> for free-preview vs enrolled-only controls and bulk regenerate.
      </p>
    </div>
  );
}
